import { clamp } from "../utils/math";
import { setTarget, slowDrift, stepSmoothed } from "../utils/smoothing";
import { SECTION_DEFINITIONS } from "./sectionDefinitions";
import type { MusicState } from "./musicTypes";

export interface ConductorInputs {
  /** User Energy, 0..1. */
  energy: number;
  /** User Density, 0..1. */
  density: number;
  rhythmicComplexity: number;
  grooveStrength: number;
  silenceProbability: number;
}

/**
 * Decides the current value of the high-level musical scalars.
 *
 * The user's sliders dominate; the section and a slow drift modulate around
 * them. That ordering matters: if the section decided energy outright, moving
 * the slider would feel broken.
 *
 * Everything moves through SmoothedScalar, so a slider change arrives over
 * bars rather than instantly.
 */
export function stepConductor(state: MusicState, inputs: ConductorInputs): void {
  const def = SECTION_DEFINITIONS[state.section.name];

  // Drift is small and slow; it exists so a held setting still breathes.
  const energyDrift = slowDrift(state.bar, 0.0) * 0.06;
  const densityDrift = slowDrift(state.bar, 1.7) * 0.05;

  setTarget(state.energy, clamp(inputs.energy + def.energyOffset + energyDrift, 0, 1));
  setTarget(state.density, clamp(inputs.density + def.densityOffset + densityDrift, 0, 1));

  // Complexity and groove follow the user directly in Phase 1A (no UI yet), with
  // a nudge from the section so develop feels busier than intro.
  const complexityOffset = state.section.name === "develop" ? 0.1 : 0;
  setTarget(state.rhythmicComplexity, clamp(inputs.rhythmicComplexity + complexityOffset, 0, 1));
  setTarget(state.grooveStrength, clamp(inputs.grooveStrength, 0, 1));

  // Breaks and intros leave more space; the genre's own silence tendency is
  // applied later, in the rhythm generator.
  const silenceOffset =
    state.section.name === "break" ? 0.25 : state.section.name === "intro" ? 0.1 : 0;
  setTarget(state.silenceProbability, clamp(inputs.silenceProbability + silenceOffset, 0, 1));

  stepSmoothed(state.energy);
  stepSmoothed(state.density);
  stepSmoothed(state.rhythmicComplexity);
  stepSmoothed(state.grooveStrength);
  stepSmoothed(state.silenceProbability);
}
